import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { TextField } from './common/InputField';
import { LoginInput } from '../types/auth.types';
import Button from './common/Button';
import AuthService from '../services/auth.service';
import { useAppContext } from '../utils/context/app-context';
import { setAuthToken } from '../utils/set-token';
import { ToastType } from '../types/common.types';
import useBrowserStorage from '../hooks/use-browser-storage';
import { useNavigate } from 'react-router-dom';
import { ButtonVariant } from '../types/button.enum';

const LoginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Required'),
  password: Yup.string().min(8).max(50).required('Password is required.'),
});

const initialValues: LoginInput = {
  email: '',
  password: '',
};

const Login = () => {
  const { setUserInfo, setToastInfo } = useAppContext();
  const { setItem } = useBrowserStorage();
  const navigate = useNavigate();

  const login = async (values: LoginInput) => {
    try {
      const response = await AuthService.signIn(values);
      if (response.data.data && response.data.data.accessToken) {
        setUserInfo({
          ...response.data.data.user,
          accessToken: response.data.data.accessToken,
        });
        setItem(
          'userInfo',
          JSON.stringify({
            ...response.data.data.user,
            accessToken: response.data.data.accessToken,
          }),
          'local'
        );
        setItem('sessionId', response.data.data?.sessionId ?? '', 'local');
        setAuthToken(response.data.data.accessToken);
        setToastInfo({
          isOpen: true,
          type: ToastType.SUCCESS,
          message: 'Logged in successfully',
        });
        navigate('/dashboard');
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: 'Internal server error, issue in login.',
        });
      }
    } catch (err) {
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Internal server error, issue in login.',
      });
    }
  };

  return (
    <div className="sm:w-[540px] border border-[#ccc] rounded-lg p-16">
      <Formik
        initialValues={initialValues}
        validationSchema={LoginSchema}
        validateOnChange={false}
        validateOnBlur={true}
        enableReinitialize={true}
        onSubmit={(values) => {
          login(values);
        }}
      >
        {({
          errors,
          touched,
          handleChange,
          handleSubmit,
          handleBlur,
          isValid,
        }) => {
          return (
            <Form>
              <div className="mb-5">
                <TextField
                  name="email"
                  label="Email"
                  placeholder="Type Email..."
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.email}
                  required={true}
                  isTouched={touched.email}
                />
              </div>
              <div className="mb-10">
                <TextField
                  name="password"
                  label="Password"
                  type="password"
                  placeholder="Enter password..."
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.password}
                  required={true}
                  isTouched={touched.password}
                />
              </div>
              <div className="text-center">
                <Button
                  type="submit"
                  disabled={!touched || !isValid}
                  onClick={() => handleSubmit()}
                  variant={ButtonVariant.SECONDARY}
                >
                  Submit
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default Login;
